import { useState } from 'react'
import { Box, CircularProgress, makeStyles, Theme } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button } from '@src/stories/Buttons/Button'
import { DownloadIcon } from '@src/icons'
import { MaterialsDirectOrdersCsvDocument } from '@src/queries/MaterialsDirectCSVQuery.generated'
import { useApolloClient } from '@apollo/client'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    marginBottom: theme.spacing(1),
    minWidth: 'auto',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '105px',
    minHeight: '40.5px',
  },
}))

const MDDownloadButton = ({ params }) => {
  const classes = useStyles()
  const client = useApolloClient()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const downloadCSVromBase64 = (base64String: string, filename?: string) => {
    const cleanBase64 = base64String.replace(/\s/g, '')
    const binaryString = atob(cleanBase64)

    const csvArray = new Uint8Array(
      Array.from(binaryString, (char: string) => char.charCodeAt(0))
    )

    const csvBlob = new Blob([csvArray], { type: 'text/csv' })
    const csvUrl = URL.createObjectURL(csvBlob)

    const link = document.createElement('a')
    link.href = csvUrl

    const sanitizedFilename = filename?.replace(/\./g, '-') || 'download.csv'
    link.setAttribute('download', sanitizedFilename)
    link.click()

    URL.revokeObjectURL(csvUrl)
  }

  const handleDownload = async () => {
    try {
      setIsLoading(true)

      const { errors, data } = await client.query({
        query: MaterialsDirectOrdersCsvDocument,

        variables: {
          input: {
            ...params,
          },
        },
      })

      if (errors || !data || data.error) throw errors

      downloadCSVromBase64(
        data.fetchMaterialsDirectOrdersCSV.fileContent,
        data.fetchMaterialsDirectOrdersCSV.fileName
      )
    } catch (error) {
      setGlobalSnackbarState({
        open: true,
        message: intl.formatMessage({
          id: 'GENERIC_ERROR',
          defaultMessage: 'Sorry, an unexpected error has occurred',
        }),
        variant: SnackbarType.Error,
        dismissableByUser: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading ? (
        <Box className={classes.loading}>
          <CircularProgress size={15} />
        </Box>
      ) : (
        <Button
          data-testid="submit-btn"
          variant="text"
          color="primary"
          type="submit"
          size="medium"
          className={classes.button}
          onClick={handleDownload}
          endIcon={<DownloadIcon />}
        >
          <FormattedMessage id="DOWNLOAD" defaultMessage="Download" />
        </Button>
      )}
    </>
  )
}

export default MDDownloadButton
